/**
 * Turn a non-OK chat response into a human-readable message.
 *
 * The chat stream endpoint is called with raw `fetch` (it needs the
 * streaming response body), so it can't lean on `jsonFetch`/`ApiError`.
 * When the request is rejected *before* the stream starts — most commonly
 * a 422 from FastAPI's request validation — the body is
 * `{ detail: [{ loc, msg, type }, ...] }`. Historically `useCarlaChat`
 * threw a bare `HTTP 422`, so the user saw a dead-end with no idea what
 * to fix and the real reason never reached observability.
 *
 * This pure helper extracts the actual reason so the inline error names
 * the problem. The most common case — the message being too long — gets
 * a first-class, actionable message instead of echoing Pydantic prose.
 */

interface FastApiValidationItem {
  loc?: unknown[]
  msg?: string
  type?: string
}

function lastLocSegment(loc: unknown[] | undefined): string | undefined {
  if (!Array.isArray(loc) || loc.length === 0) return undefined
  const seg = loc[loc.length - 1]
  return typeof seg === 'string' ? seg : undefined
}

export function humanizeChatError(status: number, body: unknown): string {
  const detail = (body as { detail?: unknown } | null)?.detail

  if (Array.isArray(detail) && detail.length > 0) {
    const items = detail as FastApiValidationItem[]

    // The message field exceeding its length cap is the common case.
    const messageTooLong = items.some(
      (it) =>
        it.type === 'string_too_long' &&
        Array.isArray(it.loc) &&
        it.loc.includes('message'),
    )
    if (messageTooLong) {
      return 'Your message is too long to send. Shorten it, or attach the content as a file.'
    }

    const first = items.find((it) => typeof it.msg === 'string')
    if (first?.msg) {
      const field = lastLocSegment(first.loc)
      return field ? `Couldn't send — ${field}: ${first.msg}` : `Couldn't send — ${first.msg}`
    }
  }

  // FastAPI returns `{ detail: "..." }` (a string) for non-validation
  // errors raised via HTTPException.
  if (typeof detail === 'string' && detail.trim()) {
    return detail
  }

  // Some routes raise HTTPException with a structured dict `detail` instead
  // of a string — e.g. /api/agent/execute-confirmed's token-failure response
  // is `{ detail: { error, confirm_token_required, confirm_token_result } }`.
  // Without this, the specific reason (already computed server-side) was
  // silently discarded and every such failure just showed "HTTP 400".
  if (
    detail &&
    typeof detail === 'object' &&
    !Array.isArray(detail) &&
    typeof (detail as { error?: unknown }).error === 'string'
  ) {
    return (detail as { error: string }).error
  }

  return `HTTP ${status}`
}
